import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Copy,
  Check,
  MessageCircle,
  Twitter,
  Facebook,
  Mail,
  Share2,
  QrCode,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable one-tap Refer & Earn share card.
 * Props:
 *   referralCode:  string — user's code (e.g., "ABCD12")
 *   referralLink:  string — full URL with ?ref=CODE
 *   headline:      optional heading override
 *   compact:       bool — if true renders a lighter version
 */
const ReferralShareCard = ({
  referralCode,
  referralLink,
  headline = "Invite Friends. Earn Together.",
  compact = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const message = `Hey! Sign up on M Ventures using my referral code ${referralCode} and get instant cashback on every purchase. Join here: ${referralLink}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — long-press the link to copy");
    }
  };

  const openShare = (channel) => {
    const encoded = encodeURIComponent(message);
    const urlEnc = encodeURIComponent(referralLink);
    let target = "";
    switch (channel) {
      case "whatsapp":
        target = `https://wa.me/?text=${encoded}`;
        break;
      case "twitter":
        target = `https://twitter.com/intent/tweet?text=${encoded}`;
        break;
      case "facebook":
        target = `https://www.facebook.com/sharer/sharer.php?u=${urlEnc}&quote=${encoded}`;
        break;
      case "email":
        target = `mailto:?subject=${encodeURIComponent("Join me on M Ventures")}&body=${encoded}`;
        break;
      case "native":
        if (navigator.share) {
          navigator
            .share({ title: "M Ventures", text: message, url: referralLink })
            .catch(() => {});
          return;
        }
        copyLink();
        return;
      default:
        return;
    }
    window.open(target, "_blank", "noopener,noreferrer");
  };

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&data=${encodeURIComponent(
    referralLink
  )}`;

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-500 via-fuchsia-500 to-pink-600 text-white shadow-lg"
      data-testid="referral-share-card"
    >
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute -top-10 -right-10 w-48 h-48 bg-white rounded-full blur-3xl" />
        <div className="absolute -bottom-14 -left-6 w-56 h-56 bg-secondary-400 rounded-full blur-3xl" />
      </div>

      <div className={`relative ${compact ? "p-4 md:p-5" : "p-5 md:p-6"}`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider text-white/70 mb-1">
              Refer & Earn
            </p>
            <h3
              className={`font-heading ${
                compact ? "text-lg" : "text-xl md:text-2xl"
              } font-bold leading-tight`}
            >
              {headline}
            </h3>
            <p className="text-white/80 text-sm mt-2 max-w-lg">
              Every friend who signs up with your code earns you multi-level
              commissions — for life.
            </p>
          </div>

          {/* Code chip + copy */}
          <div className="flex items-center gap-3">
            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2">
              <p className="text-[10px] uppercase tracking-widest text-white/60">
                Your Code
              </p>
              <p
                className="font-mono font-bold text-lg"
                data-testid="referral-code-chip"
              >
                {referralCode}
              </p>
            </div>
            <Button
              onClick={copyLink}
              className="bg-white text-primary-600 hover:bg-white/90 rounded-full px-5 shrink-0"
              data-testid="referral-copy-link-btn"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" /> Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" /> Copy Link
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Share buttons */}
        <div className="flex flex-wrap items-center gap-2 mt-5">
          <Button
            onClick={() => openShare("whatsapp")}
            className="bg-[#25D366] hover:bg-[#1fbb59] text-white rounded-full"
            size="sm"
            data-testid="referral-share-whatsapp"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp
          </Button>
          <Button
            onClick={() => openShare("twitter")}
            className="bg-black hover:bg-gray-900 text-white rounded-full"
            size="sm"
            data-testid="referral-share-twitter"
          >
            <Twitter className="w-4 h-4 mr-2" />
            Twitter
          </Button>
          <Button
            onClick={() => openShare("facebook")}
            className="bg-[#1877F2] hover:bg-[#0f65d1] text-white rounded-full"
            size="sm"
            data-testid="referral-share-facebook"
          >
            <Facebook className="w-4 h-4 mr-2" />
            Facebook
          </Button>
          <Button
            onClick={() => openShare("email")}
            variant="outline"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full"
            size="sm"
            data-testid="referral-share-email"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email
          </Button>
          <Button
            onClick={() => openShare("native")}
            variant="outline"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full"
            size="sm"
            data-testid="referral-share-native"
          >
            <Share2 className="w-4 h-4 mr-2" />
            More
          </Button>
          <Button
            onClick={() => setShowQR((v) => !v)}
            variant="outline"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full"
            size="sm"
            data-testid="referral-toggle-qr"
          >
            <QrCode className="w-4 h-4 mr-2" />
            {showQR ? "Hide QR" : "Show QR"}
          </Button>
        </div>

        {showQR && (
          <div
            className="mt-5 bg-white p-4 rounded-2xl inline-flex flex-col items-center gap-2 shadow-inner"
            data-testid="referral-qr-code"
          >
            <img src={qrSrc} alt="Referral QR code" className="w-40 h-40" />
            <p className="text-xs text-gray-600 text-center">
              Scan to open your referral link
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralShareCard;
